import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import CookieConsent from "@/components/CookieConsent";
import { filterRedirectSearch } from "@/lib/adminSections";
import LandingPage from "@/pages/LandingPage";
import EnquiryPage from "@/pages/EnquiryPage";
import AboutPage from "@/pages/AboutPage";
import FeedbackPage from "@/pages/FeedbackPage";
import ReviewsPage from "@/pages/ReviewsPage";
import CustomerPortal from "@/pages/CustomerPortal";
import PortalSetup from "@/pages/PortalSetup";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import GalleryIndexPage from "@/pages/GalleryIndexPage";
import ServicePage from "@/pages/ServicePage";
import GalleryDetailPage from "@/pages/GalleryDetailPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsConditions from "@/pages/TermsConditions";
import CookiePolicy from "@/pages/CookiePolicy";
import NotFoundPage from "@/pages/NotFoundPage";
import axios from "axios";

// Auth uses a secure httpOnly cookie. The frontend and /api are served from the
// SAME origin (preview + production), so the browser sends the cookie on every
// request automatically — no `withCredentials` needed. We deliberately do NOT
// set withCredentials globally: on this platform the ingress returns
// `Access-Control-Allow-Origin: *`, which browsers REJECT for credentialed
// cross-origin requests (e.g. a stale PWA pinned to an old preview origin),
// producing a hard "network blocked" error. Same-origin cookies are unaffected.

// Global 401 interceptor — auto-logout on expired/invalid sessions
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Protected admin routes are /admin/<section>; the /admin login route itself is excluded.
    if (error.response?.status === 401 && window.location.pathname.startsWith('/admin/')) {
      localStorage.removeItem('admin_session');
      // Phase 5: remember the attempted admin route for a safe return after login.
      // Only store a real section deep-link (never the /admin login route itself),
      // so a racing writer can't clobber it with a useless value.
      try {
        const attempted = window.location.pathname + window.location.search;
        if (/^\/admin\/[a-z0-9-]+/.test(window.location.pathname)) {
          sessionStorage.setItem('admin_return_to', attempted);
        }
      } catch (e) { /* storage unavailable — default return applies */ }
      window.location.href = '/admin';
    }
    return Promise.reject(error);
  }
);

// Phase 5: attach the signed session-bound CSRF token to unsafe admin requests.
// Enforcement is OFF by default; sending the header is harmless until enabled.
let _csrfToken = null;
export const setCsrfToken = (t) => { _csrfToken = t; };
axios.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  const url = config.url || '';
  if (_csrfToken && url.includes('/api/admin/') && ['post', 'put', 'patch', 'delete'].includes(method)) {
    config.headers = config.headers || {};
    config.headers['X-CSRF-Token'] = _csrfToken;
  }
  return config;
});

// Phase 5: re-arm the CSRF token on any authenticated /admin load (not just a
// fresh login) so enforcement can never partially activate against a reloaded
// or restored session. Silently no-ops (401) when not authenticated; inert
// while enforcement is disabled.
if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
  axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/csrf-token`)
    .then((r) => { if (r.data?.csrf_token) setCsrfToken(r.data.csrf_token); })
    .catch(() => { /* not authenticated yet — token armed at login instead */ });
}

// Legacy /admin/dashboard bookmarks land on Jobs. History-REPLACING so Back
// never returns to the obsolete URL. Only allowlisted, non-authentication
// query parameters survive the redirect (see filterRedirectSearch).
const DashboardRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/admin/jobs${filterRedirectSearch(location.search)}`} replace />;
};

function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-background">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/enquiry" element={<EnquiryPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services/:slug" element={<ServicePage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/portal" element={<CustomerPortal />} />
            <Route path="/portal/setup" element={<PortalSetup />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<DashboardRedirect />} />
            {/* Phase 4: every admin section has a stable URL. AdminDashboard
                validates :section and shows an in-layout 404 for unknown paths. */}
            <Route path="/admin/:section/*" element={<AdminDashboard />} />
            {/* Public gallery */}
            <Route path="/gallery" element={<GalleryIndexPage />} />
            <Route path="/gallery/:slug" element={<GalleryDetailPage />} />
            {/* Legal Pages */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsConditions />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            {/* Phase 6A: public catch-all — visible noindex 404 for unknown
                routes. Admin unknown sections stay inside AdminDashboard. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <CookieConsent />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </HelmetProvider>
  );
}

export default App;
